import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, TextField, MenuItem, Alert, Typography } from '@mui/material';
import {
  FormShell,
  FormActions,
  SectionLabel,
  EmptyOption,
  useLocationPicker,
  inputSx,
} from '../FormUI/RoadAccidentFormUI';
import useMunicipalityCascade from '../FormUI/useMunicipalityCascade';
import MunicipalityFields from '../FormUI/MunicipalityFields';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || 'http://127.0.0.1:8000/';

// Submitting a record now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i);

const getCurrentDateTime = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const buildInitialFormData = () => ({
  year: '',
  roadtype: '',
  location: '',
  latitude: '',
  longitude: '',
  datetime: getCurrentDateTime(),
  numberofaccidents: '',
  numberofinjured: '',
  numberoffatal: '',
});

function AddBlackspotdata() {
  const navigate = useNavigate();
  const cascade = useMunicipalityCascade();
  const [formData, setFormData] = useState(buildInitialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (latitude, longitude) => {
    setFormData((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleLocationResolved = (adm) => {
    if (adm) cascade.applyResolvedAdm(adm.adm1_code, adm.adm2_code, adm.adm3_code);
  };

  const handleLandmark = (landmark) => {
    setFormData((prev) => ({ ...prev, location: landmark || '' }));
  };

  const { latLngFields, mapElement, locationError, landmarkNotFound } = useLocationPicker({
    latitude: formData.latitude,
    longitude: formData.longitude,
    onChange: handleLocationChange,
    onLocationResolved: handleLocationResolved,
    onLandmark: handleLandmark,
    infoText: 'Click on the map to mark the accident black spot location.',
  });

  const handleReset = () => {
    setFormData(buildInitialFormData());
    cascade.reset();
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleBackClick = () => {
    navigate('/Roadaccident/Reports?tab=blackspot');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    const dataToSend = new FormData();
    dataToSend.append('year', formData.year);
    dataToSend.append('municipality', cascade.selectedMunicipality);
    dataToSend.append('submunicipality', cascade.selectedSubMunicipality);
    dataToSend.append('suco', cascade.selectedSuco);
    dataToSend.append('roadtype', formData.roadtype);
    dataToSend.append('location', formData.location);
    dataToSend.append('latitude', formData.latitude);
    dataToSend.append('longitude', formData.longitude);
    dataToSend.append('datetime', formData.datetime);
    dataToSend.append('numberofaccidents', formData.numberofaccidents);
    dataToSend.append('numberofinjured', formData.numberofinjured);
    dataToSend.append('numberoffatal', formData.numberoffatal);

    try {
      const response = await axios.post(
        `${backendUrl}roadaccident/submit_blackspot_details/`,
        dataToSend,
        { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() } }
      );
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit. Please try again.');
      }
    } catch (error) {
      setSubmitError(`An error occurred while submitting: ${error.message}`);
    }
  };

  return (
    <FormShell title="Add Black Spot Record" subtitle="Register an accident black spot" onBack={handleBackClick} onSubmit={handleSubmit}>
      {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Black Spot Details added successfully.</Alert>}
      {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

      <Box>
        <SectionLabel text="Record Details" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
          <TextField
            label="Year"
            select
            size="small"
            fullWidth
            sx={inputSx}
            name="year"
            value={formData.year}
            onChange={handleChange}
          >
            <EmptyOption text="Select a year" />
            {years.map((year) => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Road Type"
            size="small"
            fullWidth
            sx={inputSx}
            name="roadtype"
            value={formData.roadtype}
            onChange={handleChange}
            placeholder="Eg: NH, SH"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
        {/* LEFT: Location Information */}
        <Box>
          <SectionLabel text="Location Information" />

          {locationError && !(cascade.selectedMunicipality && cascade.selectedSubMunicipality && cascade.selectedSuco) && (
            <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
          )}

          <MunicipalityFields cascade={cascade} />
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            {landmarkNotFound && !formData.location && (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {latLngFields}
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box>{mapElement}</Box>
      </Box>

      <Box>
        <SectionLabel text="Case Summary" />
        <TextField
          label="Date and Time"
          type="datetime-local"
          size="small"
          fullWidth
          sx={inputSx}
          name="datetime"
          value={formData.datetime}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2.5, mt: 2.5 }}>
          <TextField
            label="Number of Accidents"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="numberofaccidents"
            value={formData.numberofaccidents}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Number of Injured"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="numberofinjured"
            value={formData.numberofinjured}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Number of Fatal"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="numberoffatal"
            value={formData.numberoffatal}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
        </Box>
      </Box>

      <FormActions onReset={handleReset} submitLabel="Submit" />
    </FormShell>
  );
}

export default AddBlackspotdata;
